import React, { useState } from 'react';
import { Stack, Button } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  GetRecurringTasksForContractDocument,
  GetRecurringTasksForContractQuery,
  GetRecurringTasksForContractQueryVariables,
  CreateRecurringTaskDocument,
  CreateRecurringTaskMutation,
  CreateRecurringTaskMutationVariables,
  UpdateRecurringTaskDocument,
  UpdateRecurringTaskMutation,
  UpdateRecurringTaskMutationVariables,
  RecurringTaskFragmentFragment,
} from '../../../generated/graphql';
import { useAlert } from '@dwelvio/react-alert';
import { AddIcon } from '../../../components/icons';
import { NewRecurringTaskDialog } from '../../../components/new-recurring-task-dialog';
import { RecurringTasksList } from '../../../components/recurring-tasks-list';

interface ContractRecurringTasksListProps {
  contractId: string;
  disabled?: boolean;
  tasks: RecurringTaskFragmentFragment[];
  onRefresh: () => void;
}

export function ContractRecurringTasksList({
  contractId,
  disabled,
  tasks,
  onRefresh,
}: ContractRecurringTasksListProps) {
  const { show } = useAlert();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<RecurringTaskFragmentFragment | null>(null);

  const [createRecurringTask, { loading: createLoading }] = useMutation<
    CreateRecurringTaskMutation,
    CreateRecurringTaskMutationVariables
  >(CreateRecurringTaskDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa vytvoriť pravidelnú úlohu', e.message),
  });

  const [updateRecurringTask, { loading: updateLoading }] = useMutation<
    UpdateRecurringTaskMutation,
    UpdateRecurringTaskMutationVariables
  >(UpdateRecurringTaskDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa upraviť pravidelnú úlohu', e.message),
  });

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-start">
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setShowAddDialog(true)} disabled={disabled}>
          Vytvor pravidelnú úlohu
        </Button>
      </Stack>

      <RecurringTasksList
        tasks={tasks}
        onRefresh={() => onRefresh()}
        onEdit={(task) => setEditingTask(task)}
        disabled={disabled}
      />

      {showAddDialog && (
        <NewRecurringTaskDialog
          open={showAddDialog}
          onClose={() => setShowAddDialog(false)}
          contractId={contractId}
          onSubmit={(input) => {
            createRecurringTask({
              variables: { input: { ...input, contractId } },
              onCompleted: () => {
                setShowAddDialog(false);
                onRefresh();
              },
            });
          }}
          loading={createLoading}
        />
      )}

      {editingTask && (
        <NewRecurringTaskDialog
          open={true}
          editTask={editingTask}
          contractId={contractId}
          onClose={() => setEditingTask(null)}
          onSubmit={(input) => {
            updateRecurringTask({
              variables: { recurringTaskId: editingTask.id, input },
              onCompleted: () => {
                setEditingTask(null);
                onRefresh();
              },
            });
          }}
          loading={updateLoading}
        />
      )}
    </Stack>
  );
}
